/* Bower — Cart drawer
 *
 * Owns:
 *   - opening / closing the drawer (click cart icon, click scrim, Esc,
 *     close button)
 *   - focus trap while the drawer is open + body scroll lock
 *   - AJAX line-item removal via /cart/change.js
 *   - re-rendering the drawer from the Section Rendering API after a
 *     cart change
 *   - keeping the header cart-count badge in sync with cart.item_count
 *   - auto-redirecting /cart -> /?cart=open (so /cart navigation opens
 *     the drawer over the homepage)
 *   - opening the drawer on load when the URL has ?cart=open
 *   - listening for `bower:cart:added` custom events from PDP / quick-add
 *     code (will land in step 7) to auto-open after add-to-cart
 *
 * Public API: window.bowerCart.open() / .close() / .refresh().
 */
(function () {
  'use strict';

  // ---- /cart -> /?cart=open redirect (runs immediately) ---------------
  if (window.location.pathname === '/cart') {
    window.location.replace(window.location.origin + '/?cart=open');
    return;
  }

  const SECTION_ID = 'cart-drawer';

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  ready(init);

  function init() {
    const drawer = document.querySelector('[data-bower-cart]');
    if (!drawer) return;

    const scrim = drawer.querySelector('[data-bower-cart-scrim]');
    const triggers = document.querySelectorAll('[data-bower-cart-toggle]');

    let lastFocus = null;

    function open() {
      // `is-open` class is the canonical "open?" state — set/cleared
      // synchronously so close()'s guard never sees stale data. The
      // visual transition is gated on the same class via CSS, but we
      // toggle it after a paint so the closed → open keyframes fire.
      // `aria-hidden` mirrors the class state for screen readers and
      // is set synchronously so external observers (a11y tooling,
      // automated tests) read truthful state without waiting on rAF.
      if (drawer.classList.contains('is-open')) return;
      lastFocus = document.activeElement;
      drawer.removeAttribute('hidden');
      drawer.setAttribute('aria-hidden', 'false');
      lockScroll();
      requestAnimationFrame(() => {
        drawer.classList.add('is-open');
        // Now that the drawer is laid out (was `hidden` until this
        // tick), re-sync the carousel arrow disabled state. On first
        // paint the carousels had 0 dimensions while the drawer was
        // hidden, which made `noOverflow` register as true and the
        // arrows hide. ResizeObserver normally catches this, but a
        // direct sync here removes the brief paint where arrows are
        // missing on the very first open.
        drawer.querySelectorAll('[data-bower-carousel]').forEach(syncCarouselArrows);
      });
      const closeBtn = drawer.querySelector('[data-bower-cart-close]');
      if (closeBtn) closeBtn.focus({ preventScroll: true });
      document.addEventListener('keydown', onKeydown);
    }

    function close() {
      if (!drawer.classList.contains('is-open')) return;
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      unlockScroll();
      document.removeEventListener('keydown', onKeydown);
      // Hide after the transition so the drawer isn't keyboard-reachable.
      // Reads the panel's actual transition-duration so the timeout
      // tracks --dur-base (or whatever it's bound to) automatically.
      const panel = drawer.querySelector('.bower-cart__panel');
      const ms = panel ? parseDuration(getComputedStyle(panel).transitionDuration) : 220;
      setTimeout(() => {
        if (!drawer.classList.contains('is-open')) {
          drawer.setAttribute('hidden', '');
        }
      }, ms + 40);
      if (lastFocus && typeof lastFocus.focus === 'function') {
        lastFocus.focus({ preventScroll: true });
      }
      // Strip the ?cart=open query so a refresh doesn't re-open.
      if (window.location.search.includes('cart=open')) {
        const url = new URL(window.location.href);
        url.searchParams.delete('cart');
        window.history.replaceState({}, '', url.toString());
      }
    }

    // Lock page scroll without horizontal layout shift.
    //
    // Locking is done on the *html* element rather than body. Setting
    // overflow:hidden on body propagates to html via the root-element
    // overflow rule, but that propagation interacts poorly with grid
    // layouts (body has display:grid in base.css) — sub-pixel shifts
    // were still observable. Locking html directly is unambiguous.
    //
    // To compensate for the disappearing scrollbar, read its actual
    // width *before* setting overflow:hidden and add it back as
    // padding-right on html. On systems with overlay scrollbars
    // (macOS default, mobile) the width reads as 0 and no padding
    // is applied — there's nothing to compensate for.
    function getScrollbarWidth() {
      return Math.max(0, window.innerWidth - document.documentElement.clientWidth);
    }
    function lockScroll() {
      const root = document.documentElement;
      const sw = getScrollbarWidth();
      if (sw > 0) root.style.paddingRight = sw + 'px';
      root.style.overflow = 'hidden';
    }
    function unlockScroll() {
      const root = document.documentElement;
      root.style.overflow = '';
      root.style.paddingRight = '';
    }

    // `transitionDuration` is comma-delimited when multiple props
    // transition; pick the longest. Returns ms.
    function parseDuration(str) {
      if (!str) return 220;
      const parts = String(str).split(',').map((s) => s.trim());
      let max = 0;
      for (const p of parts) {
        const n = parseFloat(p);
        if (Number.isNaN(n)) continue;
        const ms = p.endsWith('ms') ? n : n * 1000;
        if (ms > max) max = ms;
      }
      return max || 220;
    }

    function onKeydown(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
        return;
      }
      if (e.key === 'Tab') trapFocus(e);
    }

    function trapFocus(e) {
      const focusables = drawer.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    // ---- Triggers ----------------------------------------------------
    triggers.forEach((t) => {
      t.addEventListener('click', (e) => {
        e.preventDefault();
        open();
      });
    });

    drawer.addEventListener('click', (e) => {
      if (e.target.closest('[data-bower-cart-close]')) {
        e.preventDefault();
        close();
      }
      if (e.target === scrim) {
        close();
      }
    });

    // ---- Remove line item via AJAX -----------------------------------
    drawer.addEventListener('click', async (e) => {
      const btn = e.target.closest('[data-bower-cart-remove]');
      if (!btn) return;
      e.preventDefault();

      const key = btn.dataset.key;
      if (!key) return;

      btn.disabled = true;
      btn.textContent = 'Removing…';

      try {
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: key, quantity: 0 }),
        });
        if (!res.ok) throw new Error('cart change failed');
        const cart = await res.json();
        await refresh();
        updateCount(cart.item_count);
        document.dispatchEvent(new CustomEvent('bower:cart:changed', { detail: { cart } }));
      } catch (err) {
        btn.disabled = false;
        btn.textContent = 'Remove';
        console.error('[bower-cart] remove failed', err);
      }
    });

    // ---- Addon: Hide/Show toggle (UI only, no cart change) ----------
    drawer.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-bower-addon-toggle]');
      if (!btn) return;
      e.preventDefault();
      const addon = btn.closest('.bower-cart__addon');
      if (!addon) return;
      const collapsed = addon.classList.toggle('is-collapsed');
      btn.textContent = collapsed ? 'Show' : 'Hide';
      btn.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
    });

    // ---- Addon: tile click (optimistic + radio + abortable) ---------
    //
    // Both the card carousel and the vase carousel are radio-style:
    // at most one tile per carousel is in the cart. The tile is the
    // click target; visual state flips synchronously, network catches
    // up after.
    //
    // Behavior (mirrors the previous synchronous handler):
    //   - Click an unselected product tile: any other selected tile
    //     in the same carousel is removed, then this one is added.
    //   - Click an already-selected product tile: it's removed
    //     (deselect).
    //   - Click No-card while no card is selected: no-op.
    //   - Click No-card while a card is selected: remove that card.
    //
    // Optimistic UI: tile classes flip immediately, the addon's
    // status text updates, the header cart-count badge bumps. The
    // cart mutation runs in the background. On a network error
    // (excluding aborts) the snapshot taken at click time is
    // restored and an inline error appears in the addon header.
    //
    // Concurrency: each carousel has its own AbortController. A new
    // click in the same carousel aborts the previous in-flight
    // request — last-write-wins. Radio semantics make queueing
    // unnecessary; the user's latest click is the definitive intent.
    //
    // Removals use `/cart/update.js` keyed by variant id rather than
    // `/cart/change.js` keyed by line-item key. The line-item key
    // for the *previous* selection may not exist yet during rapid
    // clicks (the prior add hadn't returned by the time the user
    // clicked again); the variant id always exists in the rendered
    // tile data.
    const tileControllers = new WeakMap();

    drawer.addEventListener('click', async (e) => {
      const tile = e.target.closest('[data-bower-tile]');
      if (!tile) return;
      e.preventDefault();

      const kind = tile.dataset.tileKind; // 'card' | 'addon' | 'no-card'
      const variantId = tile.dataset.variantId;
      const isSelected = tile.classList.contains('is-selected');

      if (kind === 'no-card' && isSelected) return; // pure no-op

      const carousel = tile.closest('.bower-cart__carousel');
      const addon = tile.closest('.bower-cart__addon');
      if (!carousel || !addon) return;

      // Identify the variant currently in the cart for this carousel
      // by reading the rendered tile state (any tile.is-selected with
      // a data-variant-id is "in cart"; the No-card tile has no
      // variant id and is a UI-only state).
      const prev = carousel.querySelector('[data-bower-tile].is-selected[data-variant-id]');
      const prevVariantId = prev ? prev.dataset.variantId : null;

      // Compute the desired cart mutations + count delta.
      let removeVariantId = null;
      let addVariantId = null;
      if (kind === 'no-card') {
        removeVariantId = prevVariantId;
      } else if (isSelected) {
        removeVariantId = variantId; // re-click → deselect
      } else {
        if (prevVariantId && prevVariantId !== variantId) removeVariantId = prevVariantId;
        addVariantId = variantId;
      }

      let countDelta = 0;
      if (addVariantId && !removeVariantId) countDelta = 1;
      else if (removeVariantId && !addVariantId) countDelta = -1;

      // Abort any in-flight request for THIS carousel. Other carousels
      // (e.g. clicking a vase while a card request is mid-flight) keep
      // their own controllers and proceed independently.
      const prevController = tileControllers.get(carousel);
      if (prevController) prevController.abort();
      const controller = new AbortController();
      tileControllers.set(carousel, controller);

      // Snapshot state for revert on error. Taken AFTER the abort but
      // BEFORE the optimistic flip, so the snapshot represents what
      // the user saw immediately before this click.
      const snapshot = snapshotAddon(addon);
      hideAddonError(addon);

      // Apply optimistic UI synchronously.
      applyOptimisticTileState(carousel, addon, tile, kind, isSelected);
      if (countDelta !== 0) bumpCartCount(countDelta);

      try {
        if (removeVariantId) {
          const updates = {};
          updates[removeVariantId] = 0;
          const r = await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ updates }),
            signal: controller.signal,
          });
          if (!r.ok) throw new Error('cart update failed');
        }
        if (addVariantId) {
          // For card tiles, forward the textarea value so the message
          // persists onto the new line item.
          let properties = null;
          if (kind === 'card') {
            const msgField = drawer.querySelector('[data-bower-card-message-field]');
            const message = msgField ? msgField.value : '';
            if (message) properties = { _message: message };
          }
          const body = { id: addVariantId, quantity: 1 };
          if (properties) body.properties = properties;
          const r = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify(body),
            signal: controller.signal,
          });
          if (!r.ok) throw new Error('cart add failed');
        }

        // Sync server truth. The signal is threaded so a subsequent
        // click can abort this refresh too — otherwise an in-progress
        // refresh from the previous click could swap the panel
        // innerHTML and clobber the newer optimistic state.
        await refresh(controller.signal);

        const cart = await fetchCart();
        updateCount(cart.item_count);
        document.dispatchEvent(new CustomEvent('bower:cart:changed', { detail: { cart } }));

        // Clear our slot if it's still us (another click may have
        // reassigned the controller during the awaits above).
        if (tileControllers.get(carousel) === controller) {
          tileControllers.delete(carousel);
        }
      } catch (err) {
        if (err.name === 'AbortError') return; // superseded by a newer click
        console.error('[bower-cart] tile click failed', err);
        revertAddonSnapshot(addon, snapshot);
        if (countDelta !== 0) bumpCartCount(-countDelta);
        showAddonError(addon, "Couldn't update — try again.");
      }
    });

    // ---- Optimistic helpers ------------------------------------------

    function applyOptimisticTileState(carousel, addon, tile, kind, wasSelected) {
      // Strip is-selected from every other tile in this carousel —
      // radio semantics: at most one selected at a time.
      carousel.querySelectorAll('[data-bower-tile].is-selected').forEach((t) => {
        if (t !== tile) t.classList.remove('is-selected');
      });

      // No-card → just select. Product → toggle (re-click deselects).
      if (kind === 'no-card') {
        tile.classList.add('is-selected');
      } else {
        tile.classList.toggle('is-selected', !wasSelected);
      }

      updateAddonStatus(addon);

      // Card-only: toggle the message field's reveal + disabled state.
      if (addon.dataset.addon === 'card') {
        const msgWrapper = drawer.querySelector('[data-bower-card-message]');
        if (msgWrapper) {
          const cardSelected = !!carousel.querySelector(
            '[data-bower-tile][data-tile-kind="card"].is-selected'
          );
          msgWrapper.classList.toggle('is-hidden', !cardSelected);
          const textarea = msgWrapper.querySelector('textarea');
          if (textarea) textarea.disabled = !cardSelected;
        }
      }
    }

    function updateAddonStatus(addon) {
      const statusEl = addon.querySelector('[data-bower-addon-status]');
      if (!statusEl) return;
      const carousel = addon.querySelector('[data-bower-carousel]');
      if (!carousel) return;
      const sel = carousel.querySelector('[data-bower-tile][data-variant-id].is-selected');
      const addonId = addon.dataset.addon;
      if (sel) {
        const nameEl = sel.querySelector('.bower-cart__tile-name');
        statusEl.textContent = nameEl ? '— ' + nameEl.textContent.trim() + ' selected' : '';
      } else if (addonId === 'card') {
        statusEl.textContent = '— No card';
      } else {
        statusEl.textContent = '';
      }
    }

    function snapshotAddon(addon) {
      const carousel = addon.querySelector('[data-bower-carousel]');
      const tileSelected = new Map();
      if (carousel) {
        carousel.querySelectorAll('[data-bower-tile]').forEach((t) => {
          tileSelected.set(t, t.classList.contains('is-selected'));
        });
      }
      const statusEl = addon.querySelector('[data-bower-addon-status]');
      const msgWrapper = addon.dataset.addon === 'card'
        ? drawer.querySelector('[data-bower-card-message]')
        : null;
      const textarea = msgWrapper ? msgWrapper.querySelector('textarea') : null;
      return {
        tileSelected,
        statusText: statusEl ? statusEl.textContent : '',
        messageHidden: msgWrapper ? msgWrapper.classList.contains('is-hidden') : null,
        messageDisabled: textarea ? textarea.disabled : null,
      };
    }

    function revertAddonSnapshot(addon, snap) {
      snap.tileSelected.forEach((wasSelected, tile) => {
        tile.classList.toggle('is-selected', wasSelected);
      });
      const statusEl = addon.querySelector('[data-bower-addon-status]');
      if (statusEl) statusEl.textContent = snap.statusText;
      if (snap.messageHidden !== null) {
        const msgWrapper = drawer.querySelector('[data-bower-card-message]');
        if (msgWrapper) {
          msgWrapper.classList.toggle('is-hidden', snap.messageHidden);
          const textarea = msgWrapper.querySelector('textarea');
          if (textarea && snap.messageDisabled !== null) textarea.disabled = snap.messageDisabled;
        }
      }
    }

    function bumpCartCount(delta) {
      document.querySelectorAll('[data-bower-cart-count]').forEach((el) => {
        const cur = parseInt(el.textContent, 10) || 0;
        const next = Math.max(0, cur + delta);
        if (next > 0) {
          el.textContent = String(next);
          el.removeAttribute('hidden');
        } else {
          el.setAttribute('hidden', '');
        }
      });
    }

    function showAddonError(addon, msg) {
      const err = addon.querySelector('[data-bower-addon-error]');
      if (!err) return;
      err.textContent = msg;
      err.removeAttribute('hidden');
      if (err._timer) clearTimeout(err._timer);
      err._timer = setTimeout(() => {
        err.setAttribute('hidden', '');
        err.textContent = '';
        err._timer = null;
      }, 4000);
    }

    function hideAddonError(addon) {
      const err = addon.querySelector('[data-bower-addon-error]');
      if (!err) return;
      err.setAttribute('hidden', '');
      err.textContent = '';
      if (err._timer) {
        clearTimeout(err._timer);
        err._timer = null;
      }
    }

    // ---- Addon: carousel prev / next arrows -------------------------
    //
    // Each carousel-wrap renders a prev + next button positioned over
    // the strip. Click scrolls the strip by one "page" (tile width +
    // gap). Disabled state reflects scroll position so the user can
    // see when they've hit either edge. The handlers are delegated on
    // `drawer` so they survive the Section Rendering API re-render
    // that replaces the panel innerHTML on every cart change.
    const TILE_PAGE = 256; // 240px tile + 16px gap

    drawer.addEventListener('click', (e) => {
      const arrow = e.target.closest('[data-bower-carousel-prev], [data-bower-carousel-next]');
      if (!arrow) return;
      e.preventDefault();
      const wrap = arrow.closest('.bower-cart__carousel-wrap');
      const carousel = wrap ? wrap.querySelector('[data-bower-carousel]') : null;
      if (!carousel) return;
      const dir = arrow.matches('[data-bower-carousel-prev]') ? -1 : 1;
      carousel.scrollBy({ left: dir * TILE_PAGE, behavior: 'smooth' });
    });

    function syncCarouselArrows(carousel) {
      const wrap = carousel.closest('.bower-cart__carousel-wrap');
      if (!wrap) return;
      const prev = wrap.querySelector('[data-bower-carousel-prev]');
      const next = wrap.querySelector('[data-bower-carousel-next]');
      const max = carousel.scrollWidth - carousel.clientWidth;
      const atStart = carousel.scrollLeft <= 1;
      const atEnd = carousel.scrollLeft >= max - 1;
      // No overflow at all: hide both arrows. Otherwise reflect edges.
      const noOverflow = max <= 1;
      if (prev) prev.toggleAttribute('disabled', noOverflow || atStart);
      if (next) next.toggleAttribute('disabled', noOverflow || atEnd);
      if (prev) prev.style.display = noOverflow ? 'none' : '';
      if (next) next.style.display = noOverflow ? 'none' : '';
    }

    // Track ResizeObserver instances so we can disconnect them when
    // the panel innerHTML is replaced on refresh — observers attached
    // to detached carousel nodes leak otherwise.
    const carouselObservers = new Set();

    function setupCarousels() {
      // Tear down any observers from a previous render. The DOM nodes
      // they were watching are about to be (or have just been)
      // replaced by the Section Rendering API.
      carouselObservers.forEach((o) => o.disconnect());
      carouselObservers.clear();

      const carousels = drawer.querySelectorAll('[data-bower-carousel]');
      carousels.forEach((c) => {
        // Initial sync runs immediately. scrollWidth may still be 0
        // here if the carousel is inside a hidden parent or images
        // haven't laid out yet — the ResizeObserver + image load
        // fallbacks below cover that.
        syncCarouselArrows(c);
        c.addEventListener('scroll', () => syncCarouselArrows(c), { passive: true });

        // ResizeObserver fires when the carousel's content box
        // changes size — which happens when images load (the tile
        // grows from 0 height to the 4:5 aspect-ratio reservation
        // size), when the drawer panel resizes (mobile/desktop
        // breakpoint flip), and after the panel is re-rendered.
        // This is what makes the prev/next arrows appear on first
        // paint without flicker.
        if (typeof ResizeObserver !== 'undefined') {
          const ro = new ResizeObserver(() => syncCarouselArrows(c));
          ro.observe(c);
          carouselObservers.add(ro);
        }

        // Belt-and-suspenders for browsers without ResizeObserver
        // and for the initial paint where the observer's first
        // callback may not fire until well after layout settles.
        c.querySelectorAll('img').forEach((img) => {
          if (img.complete) return;
          img.addEventListener('load', () => syncCarouselArrows(c), { once: true });
          img.addEventListener('error', () => syncCarouselArrows(c), { once: true });
        });
      });
    }

    // ---- Addon: card message field (debounced persist) --------------
    let messageTimer = null;
    drawer.addEventListener('input', (e) => {
      const field = e.target.closest('[data-bower-card-message-field]');
      if (!field) return;

      // Update count locally — no network round-trip needed.
      const counter = drawer.querySelector('[data-bower-card-message-count]');
      if (counter) {
        counter.textContent = (field.value.length || 0) + ' / 200';
      }

      // Persist to the selected card line item, debounced. Note the
      // `[data-tile-kind="card"]` filter — we don't want to write a
      // message to a vase line item if one is selected.
      const selectedTile = drawer.querySelector(
        '[data-bower-tile][data-tile-kind="card"].is-selected[data-cart-item-key]'
      );
      const key = selectedTile ? selectedTile.dataset.cartItemKey : null;
      if (!key) return;

      if (messageTimer) clearTimeout(messageTimer);
      messageTimer = setTimeout(async () => {
        try {
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({
              id: key,
              properties: { _message: field.value },
            }),
          });
        } catch (err) {
          console.error('[bower-cart] message persist failed', err);
        }
      }, 600);
    });

    // ---- Cart-AJAX helpers ------------------------------------------
    async function fetchCart() {
      const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
      if (!res.ok) throw new Error('cart fetch failed');
      return res.json();
    }

    // ---- Re-render drawer via Section Rendering API ------------------
    //
    // Snapshots the user-visible scroll positions before replacing
    // the panel innerHTML and restores them after. Without this,
    // adding a card or vase would scroll the drawer body back to
    // the top, losing the user's place — a common complaint on
    // first user testing. Carousel scrollLeft is keyed by addon id
    // so a re-rendered carousel keeps the strip's horizontal
    // position stable.
    //
    // Optional `signal`: AbortController.signal threaded through from
    // an optimistic tile click. A subsequent click can abort an
    // in-progress refresh from the previous click, preventing the
    // panel innerHTML swap from clobbering newer optimistic state.
    async function refresh(signal) {
      try {
        const fetchOpts = { headers: { 'Accept': 'application/json' } };
        if (signal) fetchOpts.signal = signal;
        const res = await fetch('/?sections=' + SECTION_ID, fetchOpts);
        if (!res.ok) throw new Error('section render failed');
        const data = await res.json();
        const html = data[SECTION_ID];
        if (!html) return;
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const fresh = doc.querySelector('[data-bower-cart]');
        if (!fresh) return;

        const stalePanel = drawer.querySelector('.bower-cart__panel');
        const freshPanel = fresh.querySelector('.bower-cart__panel');
        if (!stalePanel || !freshPanel) return;

        // Snapshot scroll positions and per-addon collapsed state.
        // The Hide/Show toggle is UI-only — the server render doesn't
        // know which addons the user has collapsed, so without this
        // snapshot a remove from the bottom summary list would re-
        // expand a hidden carousel. Janky.
        const staleBody = stalePanel.querySelector('.bower-cart__body');
        const bodyScroll = staleBody ? staleBody.scrollTop : 0;
        const carouselScrolls = {};
        const collapsedAddons = new Set();
        stalePanel.querySelectorAll('.bower-cart__addon').forEach((addon) => {
          const id = addon.dataset.addon;
          if (!id) return;
          const c = addon.querySelector('[data-bower-carousel]');
          if (c) carouselScrolls[id] = c.scrollLeft;
          if (addon.classList.contains('is-collapsed')) collapsedAddons.add(id);
        });

        // Replace the panel innards in place so the open/close state stays.
        stalePanel.innerHTML = freshPanel.innerHTML;
        drawer.dataset.itemCount = fresh.dataset.itemCount;

        // Restore scroll + collapsed state on the freshly-rendered nodes.
        const newBody = drawer.querySelector('.bower-cart__body');
        if (newBody) newBody.scrollTop = bodyScroll;
        drawer.querySelectorAll('.bower-cart__addon').forEach((addon) => {
          const id = addon.dataset.addon;
          if (!id) return;
          const c = addon.querySelector('[data-bower-carousel]');
          if (c && carouselScrolls[id] != null) c.scrollLeft = carouselScrolls[id];
          if (collapsedAddons.has(id)) {
            addon.classList.add('is-collapsed');
            const toggle = addon.querySelector('[data-bower-addon-toggle]');
            if (toggle) {
              toggle.textContent = 'Show';
              toggle.setAttribute('aria-expanded', 'false');
            }
          }
        });

        // Carousels are recreated on each refresh — re-bind their
        // scroll listeners and re-sync the arrow disabled state.
        setupCarousels();
      } catch (err) {
        // Re-throw aborts so callers using a signal can detect that
        // a newer click superseded this refresh. Other errors are
        // logged but swallowed — a failed refresh shouldn't break
        // the rest of the drawer.
        if (err.name === 'AbortError') throw err;
        console.error('[bower-cart] refresh failed', err);
      }
    }

    function updateCount(n) {
      const els = document.querySelectorAll('[data-bower-cart-count]');
      els.forEach((el) => {
        if (n > 0) {
          el.textContent = String(n);
          el.removeAttribute('hidden');
        } else {
          el.setAttribute('hidden', '');
        }
      });
    }

    // ---- Auto-open hooks ---------------------------------------------
    // Open on load if URL says so (used by the /cart redirect + the
    // post-checkout-error round-trip).
    const params = new URLSearchParams(window.location.search);
    if (params.has('cart')) open();

    // Open on a successful add-to-cart event dispatched from PDPs / quick-add.
    document.addEventListener('bower:cart:added', async (e) => {
      await refresh();
      if (e && e.detail && typeof e.detail.item_count === 'number') {
        updateCount(e.detail.item_count);
      }
      open();
    });

    // First-paint carousel sync (in case the cart is non-empty on
    // page load, e.g. arriving via /cart redirect).
    setupCarousels();

    // ---- Public API --------------------------------------------------
    window.bowerCart = {
      open,
      close,
      refresh,
    };
  }
})();
